import React, { useState } from 'react'
import { Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../../../Shared/Layout/LayoutAdmin'
import Breadcrumbs from '../../../../Shared/Components/Breadcrumbs'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'

const emptyOption = (correct = false) => ({ label: '', is_correct: correct })

const emptyQuestion = () => ({
    prompt: '',
    options: [emptyOption(true), emptyOption(false), emptyOption(false), emptyOption(false)],
})

const ExamForm = ({ exam, sections, selected_section_id: selectedSectionId }) => {
    const { errors, flash } = usePage().props
    const isEdit = Boolean(exam)
    const [sectionId, setSectionId] = useState(exam?.section_id || selectedSectionId || '')
    const [title, setTitle] = useState(exam?.title || '')
    const [introduction, setIntroduction] = useState(exam?.introduction || '')
    const [closing, setClosing] = useState(exam?.closing_text || '')
    const [seconds, setSeconds] = useState(exam?.seconds_per_question || 15)
    const [questions, setQuestions] = useState(exam?.questions?.length ? exam.questions : [emptyQuestion()])
    const [saving, setSaving] = useState(false)

    const patchQuestion = (index, next) => {
        setQuestions((current) => current.map((question, i) => (i === index ? next : question)))
    }

    const setCorrect = (qIndex, oIndex) => {
        const question = questions[qIndex]
        patchQuestion(qIndex, {
            ...question,
            options: question.options.map((option, i) => ({ ...option, is_correct: i === oIndex })),
        })
    }

    const submit = (event) => {
        event.preventDefault()
        const payload = {
            sia_section_id: sectionId,
            title,
            introduction,
            closing_text: closing,
            seconds_per_question: Number(seconds) || 15,
            questions,
        }
        const options = {
            preserveScroll: true,
            onStart: () => setSaving(true),
            onFinish: () => setSaving(false),
        }
        if (isEdit) {
            Inertia.put(route('sia.manage.exams.update', exam.slug), payload, options)
            return
        }
        Inertia.post(route('sia.manage.exams.store'), payload, options)
    }

    const path = isEdit ? `/admin/sia/manage/examenes/${exam.slug}/editar` : '/admin/sia/manage/examenes/nuevo'

    return (
        <>
            <div className="mb-5 mt-5">
                <Breadcrumbs currentPath={path} />
            </div>
            <div className="d-flex justify-content-between align-items-center mb-6">
                <h1 className="fs-2 fw-bold mb-0">{isEdit ? 'Editar examen' : 'Nuevo examen'}</h1>
                <Link href={route('sia.manage.exams.index')} className="btn btn-sm btn-light">Volver</Link>
            </div>
            {flash?.error && <div className="alert alert-danger">{flash.error}</div>}
            {errors?.exam && <div className="alert alert-danger">{errors.exam}</div>}
            <Form onSubmit={submit}>
                <div className="card mb-6">
                    <div className="card-body">
                        <Form.Group className="mb-4">
                            <Form.Label>Subcategoría</Form.Label>
                            <Form.Select value={sectionId} onChange={(e) => setSectionId(e.target.value)} required disabled={isEdit}>
                                <option value="">Elige una subcategoría</option>
                                {sections.map((section) => (
                                    <option key={section.id} value={section.id}>{section.label}</option>
                                ))}
                            </Form.Select>
                            {errors?.sia_section_id && <div className="text-danger small mt-1">{errors.sia_section_id}</div>}
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label>Título</Form.Label>
                            <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} required />
                            {errors?.title && <div className="text-danger small mt-1">{errors.title}</div>}
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label>Texto de introducción</Form.Label>
                            <Form.Control as="textarea" rows={4} value={introduction} onChange={(e) => setIntroduction(e.target.value)} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Texto final</Form.Label>
                            <Form.Control as="textarea" rows={4} value={closing} onChange={(e) => setClosing(e.target.value)} />
                            <Form.Text>Se muestra al terminar, junto con la calificación. Aprobar es 80 o más.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mt-4">
                            <Form.Label>Segundos por pregunta</Form.Label>
                            <Form.Control
                                type="number"
                                min={5}
                                max={600}
                                value={seconds}
                                onChange={(e) => setSeconds(e.target.value)}
                                required
                            />
                            {errors?.seconds_per_question && <div className="text-danger small mt-1">{errors.seconds_per_question}</div>}
                            <Form.Text>Al llegar a cero sin respuesta, esa pregunta queda fallida y continúa la siguiente. El valor inicial es 15.</Form.Text>
                        </Form.Group>
                    </div>
                </div>

                {questions.map((question, qIndex) => (
                    <div className="card mb-4" key={qIndex}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h2 className="fs-5 mb-0">Pregunta {qIndex + 1}</h2>
                                {questions.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={() => setQuestions(questions.filter((_, i) => i !== qIndex))}
                                    >
                                        Quitar
                                    </button>
                                )}
                            </div>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                className="mb-4"
                                placeholder="Escribe la pregunta"
                                value={question.prompt}
                                onChange={(e) => patchQuestion(qIndex, { ...question, prompt: e.target.value })}
                                required
                            />
                            {errors?.[`questions.${qIndex}.options`] && (
                                <div className="text-danger small mb-3">{errors[`questions.${qIndex}.options`]}</div>
                            )}
                            {question.options.map((option, oIndex) => (
                                <div className="d-flex align-items-center gap-3 mb-3" key={oIndex}>
                                    <Form.Check
                                        type="radio"
                                        name={`correct-${qIndex}`}
                                        checked={option.is_correct}
                                        onChange={() => setCorrect(qIndex, oIndex)}
                                        label="Correcta"
                                    />
                                    <Form.Control
                                        value={option.label}
                                        placeholder={`Respuesta ${oIndex + 1}`}
                                        onChange={(e) => {
                                            const options = question.options.map((item, i) => (i === oIndex ? { ...item, label: e.target.value } : item))
                                            patchQuestion(qIndex, { ...question, options })
                                        }}
                                        required
                                    />
                                    {question.options.length > 2 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light"
                                            onClick={() => {
                                                let options = question.options.filter((_, i) => i !== oIndex)
                                                if (!options.some((item) => item.is_correct)) {
                                                    options = options.map((item, i) => ({ ...item, is_correct: i === 0 }))
                                                }
                                                patchQuestion(qIndex, { ...question, options })
                                            }}
                                        >
                                            Quitar
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary"
                                onClick={() => patchQuestion(qIndex, { ...question, options: [...question.options, emptyOption(false)] })}
                            >
                                Agregar respuesta
                            </button>
                        </div>
                    </div>
                ))}

                <div className="d-flex gap-3 mb-10">
                    <button type="button" className="btn btn-light-primary" onClick={() => setQuestions([...questions, emptyQuestion()])}>
                        Agregar pregunta
                    </button>
                    <Button type="submit" variant="primary" disabled={saving}>{isEdit ? 'Guardar' : 'Crear examen'}</Button>
                </div>
            </Form>
        </>
    )
}

ExamForm.layout = (page) => <Layout title={page.props.exam ? 'Editar examen' : 'Nuevo examen'}>{page}</Layout>

export default ExamForm
